export const palette = {
  midnight: {
    m700: '#2a333d',
    m600: '#44505f',
    m500: '#6a7886',
    m300: '#a3acb8',
    m200: '#d8dfe7',
    m100: '#eff2f6'
  },
  piano: {
    p600: '#424242',
    p500: '#646262',
    p300: '#9e9e9e',
    p200: '#d8d8d8',
    p100: '#f3f4f6'
  },
  dodger: {
    d900: '#0058bf',
    d800: '#0064d8',
    d500: '#0071f3',
    d400: '#4398fa',
    d300: '#bfdaf9',
    d200: '#e4f0fe',
    d100: '#f7fbff'
  },
  teal: {
    t800: '#007685',
    t400: '#00adc4',
    t300: '#00c9e4',
    t100: '#e5fcff'
  },
  frog: {
    f600: '#068051',
    f400: '#27AA36',
    f300: '#d9f0e7',
    f100: '#edf7ec'
  },
  pistachio: {
    p400: '#7fcc02',
    p100: '#ecfdd2'
  },
  supernova: {
    s400: '#c59800',
    s300: '#e4af00',
    s200: '#ffbb00',
    s100: '#fbf3d9'
  },
  orange: {
    o400: '#f88327',
    o100: '#feeddf'
  },
  violet: {
    v800: '#4438d3',
    v700: '#4e3fde',
    v600: '#675ce9',
    v300: '#d0cefd',
    v200: '#e9e9f8',
    v100: '#f9f7ff'
  },
  coral: {
    c800: '#c41e1e',
    c700: '#d12929',
    c600: '#d83b3b',
    c400: '#f27373',
    c300: '#fecfcf',
    c200: '#ffdddd',
    c100: '#fdeded'
  },
  cloud: {
    c400: '#91afd1',
    c200: '#bed2e9',
    c100: '#ecf4fb'
  },

  black: '#000000',
  white: '#ffffff',
  transparent: 'rgba(255, 255, 255, 0)'
}

export const semantic = {
  bodyBg: palette.piano.p100,
  defaultBg: palette.white,
  textColor: palette.midnight.m700,
  supportTextColor: palette.midnight.m500,
  primaryColor: palette.dodger.d500,
  secondaryColor: palette.dodger.d100,
  quietColor: palette.transparent,
  positiveColor: palette.frog.f400,
  negativeColor: palette.coral.c600,
  privateDealColor: palette.violet.v600,
  accentColor: palette.teal.t300,
  borderColor: palette.midnight.m100,
  shadowColor: palette.midnight.m700,
  anchorShadowColor: palette.dodger.d900
}

export const colors = {
  ...semantic,

  /**
   * Default values
   */
  defaultTextColor: semantic.textColor,
  defaultTitleColor: semantic.textColor,
  defaultSpinner: palette.dodger.d400,
  defaultShadow: semantic.shadowColor,
  icon: semantic.accentColor,

  /**
   * Baseline
   */
  baselineBg: semantic.bodyBg,

  /**
   * Skeleton
   */
  skeletonBg: semantic.defaultBg,
  skeletonWaveBg: semantic.defaultBg,
  skeletonElementBg: semantic.bodyBg,

  /**
   * Header
   */
  headerBgPrimary: semantic.defaultBg,
  headerBorder: semantic.borderColor,
  hamburgerColorPrimary: palette.midnight.m700,
  hamburgerActiveColorPrimary: palette.dodger.d800,
  backButtonColorPrimary: semantic.textColor,
  headerCustomContentColorPrimary: palette.piano.p600,

  /**
   * Footer
   */
  footerBg: semantic.defaultBg,
  footerColor: semantic.textColor,
  footerBorder: semantic.borderColor,

  /**
   * Links
   */
  linkColor: semantic.primaryColor,
  linkHoverColor: palette.dodger.d800,
  linkActiveColor: palette.dodger.d800,
  linkVisitedColor: palette.dodger.d800,
  linkOutlineColor: palette.dodger.d300,

  /**
   * Buttons
   */
  buttonColorPrimary: palette.white,
  buttonHoverColorPrimary: palette.white,
  buttonActiveColorPrimary: palette.white,
  buttonBgPrimary: semantic.primaryColor,
  buttonHoverBgPrimary: palette.dodger.d800,
  buttonActiveBgPrimary: palette.dodger.d900,
  buttonLoadingBulletColorPrimary: palette.white,

  buttonColorDisabled: palette.midnight.m500,
  buttonHoverColorDisabled: palette.midnight.m500,
  buttonActiveColorDisabled: palette.midnight.m500,
  buttonBgDisabled: palette.midnight.m100,
  buttonHoverBgDisabled: palette.midnight.m100,
  buttonLoadingBulletColorDisabled: palette.midnight.m500,

  buttonColorSecondary: semantic.primaryColor,
  buttonHoverColorSecondary: semantic.primaryColor,
  buttonActiveColorSecondary: semantic.primaryColor,
  buttonBgSecondary: semantic.secondaryColor,
  buttonHoverBgSecondary: palette.dodger.d200,
  buttonActiveBgSecondary: palette.dodger.d300,
  buttonBorderSecondary: palette.dodger.d300,
  buttonLoadingBulletColorSecondary: semantic.primaryColor,

  buttonColorPrivateDeal: palette.white,
  buttonHoverColorPrivateDeal: palette.white,
  buttonActiveColorPrivateDeal: palette.white,
  buttonBgPrivateDeal: semantic.privateDealColor,
  buttonHoverBgPrivateDeal: palette.violet.v700,
  buttonActiveBgPrivateDeal: palette.violet.v800,
  buttonLoadingBulletColorPrivateDeal: palette.white,

  buttonColorPrivateDealSecondary: semantic.privateDealColor,
  buttonHoverColorPrivateDealSecondary: semantic.privateDealColor,
  buttonActiveColorPrivateDealSecondary: semantic.privateDealColor,
  buttonBgPrivateDealSecondary: palette.violet.v100,
  buttonHoverBgPrivateDealSecondary: palette.violet.v200,
  buttonActiveBgPrivateDealSecondary: palette.violet.v300,
  buttonBorderPrivateDealSecondary: palette.violet.v300,
  buttonLoadingBulletColorPrivateDealSecondary: semantic.privateDealColor,

  buttonColorQuiet: semantic.textColor,
  buttonHoverColorQuiet: palette.midnight.m700,
  buttonActiveColorQuiet: palette.midnight.m700,
  buttonBgQuiet: semantic.quietColor,
  buttonHoverBgQuiet: palette.midnight.m100,
  buttonActiveBgQuiet: palette.midnight.m200,

  buttonColorWarning: semantic.negativeColor,
  buttonHoverColorWarning: semantic.negativeColor,
  buttonActiveColorWarning: semantic.negativeColor,
  buttonBgWarning: palette.coral.c100,
  buttonHoverBgWarning: palette.coral.c200,
  buttonActiveBgWarning: palette.coral.c300,
  buttonLoadingBulletColorWarning: semantic.negativeColor,

  buttonColorDelete: palette.white,
  buttonHoverColorDelete: palette.white,
  buttonActiveColorDelete: palette.white,
  buttonBgDelete: semantic.negativeColor,
  buttonHoverBgDelete: palette.coral.c700,
  buttonActiveBgDelete: palette.coral.c800,
  buttonLoadingBulletColorDelete: palette.white,

  buttonColorButtonLink: semantic.textColor,
  buttonHoverColorButtonLink: semantic.textColor,
  buttonActiveColorButtonLink: semantic.textColor,
  buttonBgButtonLink: palette.transparent,
  buttonHoverBgButtonLink: palette.cloud.c100,
  buttonActiveBgButtonLink: palette.cloud.c100,

  buttonColorWhite: semantic.textColor,
  buttonHoverColorWhite: semantic.textColor,
  buttonActiveColorWhite: semantic.textColor,
  buttonBgWhite: palette.white,
  buttonHoverBgWhite: palette.white,
  buttonActiveBgWhite: palette.white,
  buttonLoadingBulletColorWhite: semantic.textColor,
  buttonWhiteBorderColor: palette.midnight.m200,

  floatingButtonDivider: semantic.borderColor,
  floatingButtonActiveBorder: palette.midnight.m200,

  /**
   * Card
   */
  cardBg: semantic.defaultBg,
  cardShadow: semantic.shadowColor,
  cardHighlightedShadow: palette.cloud.c400,
  cardBorderColor: semantic.borderColor,

  /**
   * Tax Display
   */
  taxDisplayContentColor: semantic.supportTextColor,
  taxDisplayAndShowAllDealsLinkBorder: semantic.borderColor,

  /**
   * Rating Element
   */
  ratingElementContentColor: palette.piano.p600,

  /**
   * Offer
   */
  offer: palette.midnight.m700,
  offerBg: palette.white,
  offerBorder: semantic.borderColor,
  offerDesktop: palette.midnight.m700,
  offerHoverBg: palette.piano.p100,
  priceColor: palette.midnight.m700,
  anchorPriceColor: palette.coral.c600,
  roomsLabel: semantic.positiveColor,
  roomsLabelAlternative: palette.midnight.m500,
  availableRooms: palette.coral.c600,
  offerGradientBackground: palette.cloud.c100,
  offerHighlightedBorder: palette.dodger.d200,
  offerHighlightedPrivateBorder: palette.violet.v200,
  offerHighlightedCheapestBorder: palette.frog.f300,
  offerPrivateGradientBackground: palette.violet.v100,
  offerCheapestGradientBackground: palette.frog.f100,
  offerChevronColor: palette.midnight.m300,
  offerPrivateChevronColor: palette.violet.v600,

  /**
   * Quality Scale
   */
  qualityDefault: semantic.positiveColor,
  qualityExcellent: palette.frog.f300,
  qualityNice: palette.frog.f100,
  qualityGood: palette.pistachio.p100,
  qualityOkay: palette.supernova.s100,
  qualityAverage: palette.orange.o100,
  qualityPoor: palette.coral.c100,
  qualityBadge: palette.white,

  qualityDefaultBg: palette.frog.f100,
  qualityExcellentBg: palette.frog.f600,
  qualityNiceBg: palette.frog.f400,
  qualityGoodBg: palette.pistachio.p400,
  qualityOkayBg: palette.supernova.s300,
  qualityAverageBg: palette.orange.o400,
  qualityPoorBg: semantic.negativeColor,

  /**
   * Hotel card
   */
  hotelUnavailabilityHeader: semantic.negativeColor,
  hotelUnavailabilityIcon: semantic.negativeColor,
  hotelName: semantic.textColor,
  hotelAddress: semantic.supportTextColor,
  hotelAddressDesktop: semantic.supportTextColor,
  hotelAddressHoverBg: semantic.secondaryColor,
  lastBookedElementBorder: semantic.borderColor,
  lastBookedLabel: semantic.supportTextColor,
  lastBookedTime: palette.midnight.m700,
  showDetailsButtonBg: semantic.secondaryColor,
  showDetailsButtonBorder: palette.dodger.d300,
  reviewElementHoverBg: semantic.secondaryColor,
  unAvailabilityContentElementBorder: semantic.borderColor,
  hotelCardOfferHeadings: semantic.supportTextColor,

  /**
   * LoadingToast
   */
  loadingToastBg: semantic.accentColor,
  loadingToastColor: palette.white,

  /**
   * ErrorPopUp
   */
  ErrorPopUpBg: semantic.negativeColor,

  /**
   * Search box
   */
  searchBoxBg: semantic.defaultBg,
  searchBoxDesktopBg: 'none',
  searchBoxInputIcon: palette.midnight.m500,

  /**
   * Input
   */
  textInputBorderDefault: palette.cloud.c200,
  textInputBorderActive: palette.cloud.c200,
  textInputDefault: semantic.textColor,
  textInputHighlight: semantic.primaryColor,
  textInputIconDefault: semantic.primaryColor,
  textInputIconFocus: palette.dodger.d800,
  textInputBg: semantic.defaultBg,
  textInputPlaceholder: palette.midnight.m300,
  textInputHelperIconColor: palette.midnight.m300,
  labelInput: semantic.supportTextColor,
  textInputError: semantic.negativeColor,
  textInputErrorFocus: semantic.negativeColor,
  textInputClearButton: palette.dodger.d500,
  textVisibilityIcon: palette.midnight.m300,
  textInputIconActiveAlternative: semantic.primaryColor,
  textInputBorderAlternative: palette.cloud.c200,
  textInputBorderActiveAlternative: palette.dodger.d400,
  textInputHighlightAlternative: palette.dodger.d400,
  textInputFocusBgAlternative: palette.dodger.d200,
  textInputSeparatorBg: palette.cloud.c200,
  textInputSeparatorActiveBg: palette.dodger.d300,

  backgroundInputDisabled: palette.midnight.m100,
  borderInputDisabled: palette.cloud.c200,
  textInputDisabled: palette.midnight.m500,

  /**
   * FakeInput
   */
  fakeInput: semantic.textColor,
  fakeInputBg: semantic.secondaryColor,
  fakeInputBorder: palette.cloud.c200,
  fakeInputIcon: palette.dodger.d400,
  fakeInputPlaceholder: palette.midnight.m300,
  fakeInputHighlight: semantic.primaryColor,
  fakeInputHighlightBg: palette.dodger.d200,
  fakeInputHighlightBorder: palette.dodger.d400,

  /**
   * Select box
   */
  selectBoxBorderDefault: palette.cloud.c200,
  selectBoxBorderActive: palette.cloud.c200,
  selectBox: semantic.textColor,
  selectBoxBg: semantic.defaultBg,

  /**
   * Toggle
   */
  toggleCheckedBg: palette.dodger.d400,
  toggleUncheckedBg: palette.piano.p200,
  toggleKnobCheckedBg: palette.dodger.d500,
  toggleKnobUncheckedBg: palette.dodger.d100,
  toggleImageSource: palette.white,
  toggleFocusShadow: palette.dodger.d800,
  toggleBoxShadow: palette.black,

  /**
   * GuestPicker
   */
  guestPickerHeader: semantic.textColor,
  guestPickerHeaderShadow: palette.midnight.m100,
  guestPickerHeaderButton: semantic.primaryColor,
  guestPickerHeaderButtonHover: palette.dodger.d800,
  guestPickerContent: semantic.textColor,
  guestPickerContentDivider: semantic.borderColor,
  guestPickerBorder: palette.dodger.d300,
  guestPickerBg: semantic.secondaryColor,
  guestPickerRemoveColor: palette.coral.c600,
  guestPickerInputColor: palette.black,
  guestPickerInputBorder: palette.cloud.c200,
  guestPickerInputBorderHover: palette.piano.p300,
  guestPickerInputBorderFocus: palette.dodger.d500,
  guestButtonBg: semantic.secondaryColor,
  guestButtonBorder: palette.dodger.d300,
  guestSelectArrow: semantic.primaryColor,

  /**
   * CompactSearchSummary
   */
  compactSearchSummaryPrimary: semantic.textColor,
  compactSearchSummarySecondary: semantic.supportTextColor,
  compactSearchSummaryBorder: palette.cloud.c200,
  compactSearchSummaryBg: semantic.secondaryColor,
  compactSearchSummaryIcon: semantic.primaryColor,

  /**
   * AutoSuggests
   */
  autoSuggestBorder: semantic.borderColor,
  autoSuggestIcon: palette.dodger.d400,
  autoSuggestText: semantic.textColor,
  autoSuggestSearchParams: semantic.supportTextColor,
  autoSuggestItemBg: semantic.defaultBg,
  autoSuggestItemHighlightedBg: palette.piano.p100,
  autoSuggestPlaceLabel: semantic.accentColor,
  autoSuggestPlaceLabelBg: palette.dodger.d100,
  autoSuggestParent: semantic.supportTextColor,
  autoSuggestErrorText: semantic.negativeColor,
  userHistroryHeading: semantic.supportTextColor,
  userHistroryParams: palette.teal.t100,

  /**
   * DatePicker
   */
  datePickerBg: semantic.defaultBg,
  datePickerBorder: palette.dodger.d300,
  datePickerCaption: semantic.textColor,
  datePickerIcon: semantic.primaryColor,
  datePickerWeekDay: palette.midnight.m500,
  datePickerDay: semantic.primaryColor,
  datePickerDayBg: palette.midnight.m100,
  datePickerDayDisabled: palette.midnight.m300,
  datePickerDayDisabledBg: palette.midnight.m100,
  datePickerDaySelected: palette.white,
  datePickerDaySelectedBg: palette.dodger.d400,
  datePickerDayStartEnd: palette.white,
  datePickerDayStartEndBg: palette.dodger.d800,
  datePickerWeekEndDay: semantic.primaryColor,
  datePickerWeekEndDayBg: palette.dodger.d300,
  datePickerWeekEndDisabledDay: palette.midnight.m300,
  datePickerWeekEndDayDisabledBg: palette.dodger.d300,
  datePickerHover: palette.white,
  datePickerHoverBg: semantic.primaryColor,
  datePickerTodayBorder: semantic.primaryColor,
  datePickerInputBg: semantic.defaultBg,
  datePickerSelectedInputBg: palette.dodger.d200,
  datePickerButtonColor: palette.midnight.m600,

  /**
   * Tabs
   */
  tabsShadow: semantic.shadowColor,
  tabBorder: palette.piano.p100,
  tabBorderDesktop: palette.midnight.m100,
  tabActiveBorder: semantic.accentColor,
  tabBg: semantic.defaultBg,
  tabMobile: palette.midnight.m700,
  tabDesktop: semantic.primaryColor,
  tabDesktopActive: palette.midnight.m700,

  /**
   * Gallery
   */
  galleryControls: palette.white,
  galleryControlsFilter: palette.black,
  galleryBg: palette.midnight.m100,
  galleryThumbBorderActive: semantic.primaryColor,
  galleryThumb: palette.white,
  galleryThumbBg: palette.midnight.m700,

  /**
   * Hotel Details
   */
  detailsBg: palette.piano.p100,
  detailsCardBg: semantic.defaultBg,
  infoAndReviewsWrapperBg: semantic.defaultBg,
  infoAndReviewsWrapperShadow: semantic.shadowColor,
  reviewsCount: semantic.supportTextColor,
  placeName: semantic.supportTextColor,
  locationAddressBarBg: semantic.defaultBg,
  locationAddressBar: palette.midnight.m700,
  locationAddressIcon: semantic.accentColor,
  locationAddressIconBg: palette.teal.t100,
  detailsName: palette.midnight.m700,
  detailsNameIcon: semantic.accentColor,
  detailsNameArrowIcon: semantic.primaryColor,
  detailsNameBorder: semantic.borderColor,
  detailsContent: palette.midnight.m700,
  reviewsLabel: palette.midnight.m700,
  travelersPercentageDesktop: palette.midnight.m700,
  mapSummaryCardPricesLabel: palette.midnight.m500,
  mapSummaryCardPictureOverlayText: palette.white,
  /**
   * Overlay
   */
  overlayBg: palette.midnight.m700,
  overlayContentBg: semantic.defaultBg,
  overlayWrapperBg: semantic.defaultBg,
  overlayCloseButton: palette.midnight.m600,
  overlayFooterBg: semantic.defaultBg,
  overlayFooterSubtitleColor: semantic.supportTextColor,
  overlayHeaderSeparator: palette.midnight.m100,
  overlayHeaderBg: semantic.defaultBg,
  guestPickerOverlayBg: semantic.defaultBg,

  /**
   * Navigation
   */
  navMenuLink: palette.midnight.m700,
  navMenuBorder: semantic.borderColor,
  navMenuIcon: semantic.primaryColor,

  /**
   * Currency/language selectors
   */
  preferenceSelectorLabel: semantic.supportTextColor,
  preferenceSelectorBg: semantic.secondaryColor,
  preferenceSelectorBorder: palette.dodger.d300,
  preferenceSelectorActiveBg: semantic.defaultBg,
  preferenceSelectorActiveBorder: semantic.primaryColor,
  preferenceSelectorActiveShadow: semantic.primaryColor,
  preferenceSelectorSymbol: semantic.primaryColor,
  preferencePopupBg: semantic.defaultBg,
  preferencePopupHeaderBorder: semantic.borderColor,
  preferencePopupPopularBg: palette.midnight.m100,
  preferencePopupActiveBg: palette.dodger.d400,
  preferencePopupActive: palette.white,
  preferencePopupSymbol: palette.midnight.m700,
  preferencePopupFlagShadow: semantic.shadowColor,

  /**
   * Popup color overrides
   * This should be cleaned up / migrated after experiment dq82hd-desktop-map
   */
  popupCloseBtnColor: semantic.textColor,

  /**
   * Sort by
   */
  sortByDefault: palette.midnight.m700,
  sortByActive: palette.white,
  sortByBorderDefault: palette.cloud.c200,
  sortByBorderActive: palette.teal.t400,
  sortByBgDefault: palette.white,
  sortByBgActive: semantic.accentColor,
  sortByIconDefault: palette.midnight.m200,
  sortByIconActive: palette.white,
  sortByDesktop: semantic.primaryColor,
  sortByLabelDesktop: palette.midnight.m700,
  sortByActiveDesktop: palette.midnight.m700,
  sortByBorderDesktop: semantic.accentColor,

  /**
   * Free cancellation filter
   */
  freeCancellationFilterColor: palette.midnight.m700,
  freeCancellationFilterHighlightedColor: semantic.positiveColor,

  /**
   * Banner
   */
  bannerColor: palette.white,
  bannerBackground: semantic.primaryColor,

  /**
   * Error Banner
   */
  errorBannerColor: palette.white,
  errorBannerBackground: semantic.negativeColor,

  /**
   * Default Dates Banner
   */
  defaultDatesBannerColor: palette.white,
  defaultDatesBannerBg: palette.midnight.m700,

  /**
   * Filters Overlay
   */
  filtersWrapperBorder: semantic.borderColor,
  filtersBorder: semantic.borderColor,
  filtersTitle: semantic.textColor,
  filtersButton: palette.white,
  filtersButtonDisabled: palette.midnight.m300,
  filtersButtonDisabledBorder: semantic.borderColor,
  filtersButtonDisabledBg: semantic.defaultBg,
  filtersFooterShadow: semantic.shadowColor,
  filtersFooterBg: semantic.defaultBg,
  starRatingBg: palette.supernova.s200,
  facetFilter: palette.midnight.m700,
  facetFilterIcon: semantic.primaryColor,
  facetFilterCount: semantic.supportTextColor,
  mapFilterActivityIndicator: semantic.primaryColor,
  mapFilterActivityIndicatorBorder: palette.white,

  /**
   * Floating search controls
   */
  floatingSearchControlsIconColor: palette.dodger.d500,
  floatingSearchControlsColor: palette.dodger.d500,
  floatingSearchControlsPillDividerColor: palette.dodger.d300,
  floatingSearchControlsBg: palette.white,

  /**
   * Segmented Control
   */
  segmentedControlBorder: palette.midnight.m200,
  segmentedControlActiveBg: palette.dodger.d100,
  segmentedControlActiveBorder: palette.dodger.d500,

  /**
   * Checkbox
   */
  checkboxHover: palette.midnight.m700,
  checkboxBg: semantic.defaultBg,
  checkboxBorder: palette.midnight.m200,
  checkboxBorderB: palette.midnight.m200,
  checkboxShadow: palette.cloud.c200,
  checkboxIcon: palette.white,
  checkboxIconB: palette.dodger.d500,
  checkboxIconHover: palette.cloud.c200,
  checkboxIconCheckedBg: palette.dodger.d500,
  checkboxIconCheckedBgB: palette.dodger.d500,
  checkboxIconDisabled: palette.midnight.m300,
  checkboxIconDisabledBg: palette.midnight.m100,
  checkboxIconFocus: palette.dodger.d300,
  checkboxLabelColor: palette.midnight.m700,
  checkboxErrorOutline: palette.coral.c300,
  checkboxSecondaryLabelColor: palette.midnight.m500,

  /**
   * Radio
   */
  radioBg: semantic.defaultBg,
  radioBorder: palette.midnight.m200,
  radioIcon: palette.white,
  radioIconCheckedBg: palette.dodger.d500,
  radioIconDisabled: palette.midnight.m300,
  radioIconDisabledBg: palette.midnight.m100,
  radioLabelColor: palette.midnight.m700,
  radioSecondaryLabelColor: palette.midnight.m500,

  /**
   * Filters Bar
   */
  filtersBarBg: semantic.defaultBg,

  /**
   * Loading Toast
   */
  loadingToast: palette.white,

  /**
   * Homepage
   */
  homePage: semantic.textColor,
  homePageBg: semantic.defaultBg,

  /**
   * Rating
   */
  ratingLabel: palette.midnight.m700,

  /**
   * PopupOnExit
   */
  popupOnExitBg: semantic.bodyBg,
  popupOnExitShadow: palette.black,

  /**
   * Best Deal Modal
   */
  bestDealModalHeader: palette.white,
  bestDealModalHeaderBg: palette.orange.o400,
  bestDealModalBg: semantic.defaultBg,
  bestDealModalTitle: semantic.textColor,
  bestDealModalClose: palette.white,
  bestDealModalCloseHover: palette.piano.p100,

  /**
   * SearchUnavailability
   */
  searchUnavailability: palette.midnight.m700,

  /**
   * YourChoiceLabel
   */
  yourChoiceLabel: semantic.positiveColor,
  yourChoiceLabelBg: palette.frog.f100,

  /**
   * Tooltip
   */
  tooltipPrimaryBg: semantic.defaultBg,
  tooltipSecondaryBg: semantic.primaryColor,
  tooltipDarkBg: palette.midnight.m700,
  tooltipIcon: semantic.accentColor,
  tooltipSecondaryColor: palette.white,

  /**
   * Popover
   */
  popoverBg: semantic.defaultBg,
  popoverBorder: palette.midnight.m200,

  /**
   * GreatDealTag
   */
  greatDeal: palette.white,
  greatDealBg: palette.orange.o400,

  /**
   * LowestPrice
   */
  lowestPrice: palette.frog.f600,
  lowestPriceTagBackground: palette.frog.f100,
  lowestPriceTagText: palette.frog.f600,

  /**
   * PrivateDeal
   */
  privateDealTagText: palette.white,
  privateDeal: semantic.privateDealColor,
  privateDealAccountTag: palette.violet.v200,

  privateDealBgBase: semantic.privateDealColor,
  privateDealBgDarker: palette.violet.v800,

  /**
   * Sign In
   */
  signInButton: semantic.primaryColor,
  signInButtonBorder: semantic.primaryColor,
  signInOverlayHeaderBorder: semantic.borderColor,
  signInOverlayCloseButton: palette.midnight.m700,
  signInOverlayDivider: palette.midnight.m300,
  signInOverlayDividerBg: semantic.defaultBg,
  signInOverlayDividerLine: semantic.borderColor,
  signInOverlayRegister: palette.midnight.m700,

  /**
   * Avatar
   */
  avatarBackground: palette.midnight.m100,
  avatar: semantic.primaryColor,
  avatarDefaultPictureBg: palette.midnight.m100,
  avatarInitials: palette.midnight.m600,
  avatarPlusBorder: palette.violet.v300,

  /**
   * User account side menu
   */
  userSideMenuBorder: semantic.borderColor,
  userSideMenuActiveBorder: semantic.accentColor,
  userSideMenuActive: palette.midnight.m700,

  /**
   * User authenticated dowpdown menu
   */
  linkBgColorHover: palette.cloud.c100,
  /**
   * User account card
   */
  userAccountCardIcon: semantic.accentColor,
  userDeleteAccountButton: semantic.negativeColor,
  userEmailValidationIcon: semantic.negativeColor,
  userEmailValidation: palette.midnight.m700,

  /**
   * Input colours
   */
  inputColor: semantic.textColor,
  inputLabelColor: palette.midnight.m300,
  inputBg: semantic.defaultBg,
  inputBorder: palette.midnight.m200,
  inputBorderHover: palette.midnight.m300,
  inputBorderFocused: palette.dodger.d300,
  inputBorderActive: palette.dodger.d500,
  inputErrorBorder: semantic.negativeColor,
  inputCaret: semantic.accentColor,
  inputValidationErrorColor: semantic.negativeColor,
  inputValidationPassColor: semantic.positiveColor,
  inputVerifiedColor: palette.frog.f600,
  inputHighlight: semantic.primaryColor,
  inputIcon: semantic.primaryColor,
  inputDisabledColor: palette.midnight.m300,
  inputDisabledBorder: palette.midnight.m200,
  inputDisabledBg: palette.midnight.m100,
  inputPlaceholderIcon: palette.cloud.c200,

  /**
   * Toast
   */
  notificationToastSuccessBg: semantic.positiveColor,
  notificationToastErrorBg: semantic.negativeColor,

  /**
   * Delete account modal
   */
  deleteAccountModal: semantic.textColor,
  deleteAccountModalTitle: semantic.textColor,
  deleteAccountModalClose: semantic.primaryColor,
  deleteAccountModalTitleBorder: semantic.borderColor,
  deleteAccountButtonBg: palette.coral.c100,
  deleteAccountButton: semantic.negativeColor,

  /**
   * Profile Links
   */
  profileLinksBorder: semantic.borderColor,
  profileLinks: semantic.textColor,

  /**
   * User email validation modal
   */
  emailValidationModal: semantic.textColor,
  emailValidationModalTitle: semantic.textColor,
  emailValidationModalClose: semantic.primaryColor,
  emailValidationModalTitleBorder: semantic.borderColor,
  emailVerificationModalShadow: palette.black,

  /**
   * Range slider
   */
  rangeSliderRail: semantic.borderColor,
  rangeSliderTrack: palette.teal.t400,
  rangeSliderHandleBg: palette.white,
  rangeSliderHandleBorder: palette.teal.t400,
  rangeSliderHandleActiveShadow: palette.teal.t100,
  // clean up or migrate colors after dq82hd-desktop-map experiment
  rangeSliderRailBSide: semantic.borderColor,
  rangeSliderTrackBSide: palette.dodger.d500,
  rangeSliderHandleBgBSide: palette.white,
  rangeSliderHandleBorderBSide: semantic.borderColor,

  /**
   * Alert
   */
  alert: {
    info: {
      background: palette.dodger.d500,
      color: palette.white,
      icon: palette.white
    },
    infoLight: {
      background: palette.dodger.d200,
      color: palette.midnight.m700,
      icon: palette.dodger.d500
    },
    success: {
      background: palette.frog.f600,
      color: palette.white,
      icon: palette.white
    },
    successLight: {
      background: palette.frog.f100,
      color: palette.midnight.m700,
      icon: palette.frog.f600
    },
    warning: {
      background: palette.supernova.s200,
      color: palette.midnight.m700,
      icon: palette.midnight.m700
    },
    warningLight: {
      background: palette.supernova.s100,
      color: palette.midnight.m700,
      icon: palette.supernova.s400
    },
    error: {
      background: palette.coral.c600,
      color: palette.white,
      icon: palette.white
    },
    errorLight: {
      background: palette.coral.c100,
      color: palette.midnight.m700,
      icon: palette.coral.c600
    },
    privateDeal: {
      background: palette.violet.v600,
      color: palette.white,
      icon: palette.white
    },
    privateDealLight: {
      background: palette.violet.v200,
      color: palette.midnight.m700,
      icon: palette.violet.v700
    },
    neutral: {
      background: palette.midnight.m500,
      color: palette.white,
      icon: palette.white
    },
    neutralLight: {
      background: palette.midnight.m100,
      color: palette.midnight.m500,
      icon: palette.midnight.m500
    },
    danger: {
      background: palette.coral.c700,
      border: palette.coral.c700,
      color: palette.coral.c100,
      icon: palette.coral.c100
    }
  },

  /**
   * Histogram
   */
  histogramItemBg: palette.midnight.m100,
  histogramItemActiveBg: palette.teal.t400,
  // clean up or migrate colors after dq82hd-desktop-map experiment
  histogramItemBgBSide: palette.midnight.m100,
  histogramItemActiveBgBSide: palette.dodger.d300,
  histogramBorderColorBSide: palette.white,

  /**
   * CookieBanner
   */
  cookieBannerBorder: semantic.borderColor,
  cookieBannerBackground: semantic.defaultBg,
  cookieBannerColor: palette.piano.p600,
  cookieBannerBtnColor: semantic.primaryColor,
  cookieBannerLinkColor: palette.midnight.m700,

  cookieProBannerBackground: palette.midnight.m700,
  cookieProBannerColor: palette.white,

  /**
   * Map
   */
  mapPin: palette.orange.o400,
  mapMarkerBg: palette.white,
  mapMarkerSelectedBg: palette.dodger.d500,
  mapMarkerSelectedColor: palette.white,
  mapMarkerVisitedBg: palette.midnight.m100,
  mapMarkerVisitedBorder: palette.midnight.m300,
  mapMarkerAnchorBorder: palette.dodger.d400,
  mapMarkerBlueDotBg: palette.dodger.d500,
  mapMarkerBlueDotBorder: palette.dodger.d300,
  mapHeaderBg: palette.white,
  mapHeaderIconColor: palette.midnight.m700,
  mapHeaderDestinationColor: palette.midnight.m700,
  mapHeaderDatesColor: palette.midnight.m500,
  mapNoResultsIconColor: palette.midnight.m700,

  /**
   * Map Hotel Card
   */
  hotelSummaryCardHotelNameColor: palette.midnight.m600,

  /**
   * checkout colors
   */
  selectArrowColor: semantic.primaryColor,
  closeBtnColor: semantic.primaryColor,
  closeBtnColorHover: palette.dodger.d800,
  returnToSearchBtnColor: semantic.primaryColor,
  openNewColor: semantic.primaryColor,

  secureBookingSectionBg: palette.teal.t100,

  roomTotalPrimarySectionColor: palette.white,
  roomTotalPrimarySectionBg: semantic.primaryColor,
  roomTotalSecondarySectionColor: palette.midnight.m700,
  roomTotalSecondarySectionBg: palette.transparent,

  roomTotalDisclaimerPrimarySectionColor: palette.white,
  roomTotalDisclaimerPrimarySectionBg: palette.dodger.d400,
  roomTotalDisclaimerSecondarySectionColor: palette.midnight.m700,
  roomTotalDisclaimerSecondarySectionBg: palette.transparent,

  promotionBannerCheapest: palette.white,
  promotionBannerCheapestBg: semantic.positiveColor,
  promotionBannerExpensive: palette.midnight.m700,
  promotionBannerExpensiveBg: palette.white,
  promotionBannerParity: palette.white,
  promotionBannerParityBg: semantic.primaryColor,
  promotionBannerPrivate: palette.white,
  promotionBannerPrivateBg: semantic.privateDealColor,

  promotionComparisonHeaderExpensive: palette.white,
  promotionComparisonHeaderExpensiveBg: semantic.primaryColor,
  promotionComparisonSoldOutArrow: semantic.primaryColor,
  promotionPrivateDealBg: palette.violet.v200,

  promotionComparisonArrowRight: palette.midnight.m600,

  bookingIdBorder: palette.midnight.m300,
  bookingIdBg: palette.midnight.m100,

  toasterBg: palette.midnight.m500,

  priceRaiseBanner: palette.supernova.s100,
  priceDropBanner: palette.frog.f300,
  priceChangeBanner: palette.piano.p100,
  providerOfferPriceColor: palette.frog.f600,
  priceMismatchLabel: palette.midnight.m600,

  savingTagLabel: palette.white,
  savingTagBg: semantic.positiveColor,
  savingTagPrivateLabel: palette.white,
  savingTagPrivateBg: semantic.privateDealColor,

  totalSaving: palette.white,
  totalSavingBg: semantic.positiveColor,
  totalSavingPrivate: palette.white,
  totalSavingPrivateBg: semantic.privateDealColor,

  savingBannerLabel: semantic.positiveColor,
  savingBannerBg: palette.frog.f100,
  savingBannerPrivateLabel: semantic.privateDealColor,
  savingBannerPrivateBg: palette.violet.v200,

  /**
   * checkout colors end
   */

  /**
   * booking management colors
   */
  bookingIntroBg: semantic.secondaryColor,
  /**
   * booking management colors end
   */

  /**
   * PrimaryOffer, new room selection page
   */
  offerVariant: {
    loading: {
      labelTextColor: palette.midnight.m100,
      labelBgColor: palette.midnight.m100,
      gradient: palette.white
    },
    popular: {
      labelTextColor: palette.dodger.d900,
      labelBgColor: palette.dodger.d300,
      gradient: palette.cloud.c100
    },
    best: {
      labelTextColor: palette.frog.f600,
      labelBgColor: palette.frog.f300,
      gradient: palette.frog.f100
    },
    member: {
      labelTextColor: palette.violet.v800,
      labelBgColor: palette.violet.v200,
      gradient: palette.violet.v100
    }
  },

  /**
   * Lightbox gallery
   */
  lightbox: {
    backgroundColor: palette.black,
    dotIndicatorColor: palette.white,
    paneAmountTextColor: palette.white
  },

  /**
   * Filter Pill
   */
  pill: {
    buttonText: semantic.textColor,
    buttonTextDisabled: palette.midnight.m300,
    buttonBorder: palette.midnight.m200,
    buttonBorderHover: palette.midnight.m300,
    buttonBorderActiveFocus: palette.dodger.d500,
    buttonBackground: palette.white,
    buttonBackgroundActive: palette.dodger.d100,
    buttonBackgroundDisabled: palette.midnight.m100,
    buttonFocus: palette.dodger.d500,
    iconColor: palette.midnight.m600,
    iconColorDisabled: palette.midnight.m500,
    countColor: palette.white,
    countBackground: palette.dodger.d500,
    countBackgroundDisabled: palette.midnight.m300
  },

  /**
   * Filter Display
   */
  filterDisplayContainer: {
    dividerColor: palette.midnight.m200
  },

  /**
   * Filter Scroll Wrapper
   */
  filterScrollWrapper: {
    buttonBgColor: palette.white,
    buttonIconColor: palette.midnight.m700,
    buttonGradientColor: palette.white,
    buttonGradientTransparent: palette.transparent
  },

  /**
   * Cancellation Penalties
   */
  cancellationPenalties: {
    freeCancellationBg: palette.frog.f100,
    freeCancellationText: palette.frog.f400,
    partialRefundBg: palette.supernova.s100,
    partialRefundCircle: palette.supernova.s200,
    partialRefundText: palette.supernova.s400,
    nonRefundableCircle: palette.coral.c600,
    nonRefundableBg: palette.midnight.m100,
    nonRefundableText: palette.midnight.m500,
    timeTextColor: semantic.supportTextColor,
    borderColor: palette.midnight.m200
  },

  /**
   * Share button
   */
  shareButtonBg: semantic.defaultBg,

  /**
   * Hotel card B side
   * TODO clean up after dq82hd-desktop-map experiment
   */
  hotelCard: {
    baseBorder: palette.white,
    anchorBorder: palette.dodger.d400,
    divider: palette.midnight.m100,
    checkInOutText: palette.midnight.m500,
    labelDivider: palette.midnight.m300,
    roomsLabel: palette.midnight.m600,
    background: palette.white,
    offerHoverBg: palette.cloud.c100
  },

  /** Reviews */
  sentimentTextColor: palette.frog.f600,
  sentimentBgColor: palette.frog.f300,

  guestTypeIcon: palette.midnight.m700,
  guestTypeBg: palette.midnight.m100,
  guestTypePercentage: palette.midnight.m500,

  /** Tag */
  tag: {
    success: {
      background: palette.frog.f600,
      color: palette.frog.f100,
      icon: palette.frog.f100
    },
    successLight: {
      background: palette.frog.f100,
      color: palette.frog.f600,
      icon: palette.frog.f600
    },
    privateDeal: {
      background: palette.violet.v600,
      color: palette.violet.v200,
      icon: palette.violet.v200
    },
    privateDealLight: {
      background: palette.violet.v200,
      color: palette.violet.v700,
      icon: palette.violet.v700
    },
    neutral: {
      background: palette.piano.p100,
      color: palette.piano.p600,
      icon: palette.piano.p600
    },
    priceDrop: {
      background: palette.orange.o400,
      color: palette.orange.o100,
      icon: palette.orange.o100
    }
  },
  /** Accordion */
  accordion: {
    arrowColor: semantic.primaryColor
  }
}
